import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VendorLayout from '@/components/layouts/VendorLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Send, Loader2, Plus, X, Building2 } from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const CATEGORIES = [
  "Payment Processing",
  "Marketing & Advertising",
  "Fulfillment & Logistics",
  "Customer Service",
  "Analytics & Data",
  "Security & Fraud",
  "Inventory Management",
  "CRM & Sales",
  "HR & Operations",
  "Other"
];

interface FormData {
  target_company_id: string;
  product_name: string;
  pitch_content: string;
  category: string;
  claims: string[];
}

export default function NewPitchPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [merchants, setMerchants] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    target_company_id: '',
    product_name: '',
    pitch_content: '',
    category: '',
    claims: []
  });
  const [newClaim, setNewClaim] = useState('');

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const response = await fetch(getApiUrl('/api/pitches/merchants/list'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMerchants(data);
      }
    } catch (err) {
      console.error('Failed to load merchants:', err);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (value: string) => {
    setFormData({ ...formData, category: value });
  };

  const handleMerchantChange = (value: string) => {
    setFormData({ ...formData, target_company_id: value });
  };

  const addClaim = () => {
    if (newClaim.trim() && formData.claims.length < 5) {
      setFormData({ ...formData, claims: [...formData.claims, newClaim.trim()] });
      setNewClaim('');
    }
  };

  const removeClaim = (index: number) => {
    setFormData({
      ...formData,
      claims: formData.claims.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', getApiUrl('/api/pitches/vendor/submit'), true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.onload = function () {
        setLoading(false);
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            setSuccess(true);
            setTimeout(() => navigate('/vendor/pitches'), 2000);
          } else {
            setError(data.detail || 'Failed to submit pitch');
          }
        } catch (e) {
          setError('Invalid server response');
        }
      };

      xhr.onerror = function () {
        setLoading(false);
        setError('Network error');
      };

      xhr.send(JSON.stringify(formData));
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (success) {
    return (
      <VendorLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-emerald-800 mb-2">Pitch Submitted!</h2>
            <p className="text-emerald-600">Your pitch is now pending evaluation.</p>
          </div>
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="max-w-2xl mx-auto" data-testid="new-pitch-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Submit New Pitch</h1>
          <p className="text-stone-600 mt-1">Send your product pitch to a merchant for evaluation</p>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-sm">
                {error}
              </div>
            )}

            {/* Target Merchant */}
            <div className="space-y-2">
              <Label className="text-stone-700">Target Merchant *</Label>
              {loadingMerchants ? (
                <div className="h-11 flex items-center justify-center bg-stone-50 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                </div>
              ) : (
                <Select onValueChange={handleMerchantChange} required>
                  <SelectTrigger data-testid="merchant-select" className="h-11">
                    <SelectValue placeholder="Select a merchant" />
                  </SelectTrigger>
                  <SelectContent>
                    {merchants.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-stone-400" />
                          {m.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Product Name */}
            <div className="space-y-2">
              <Label htmlFor="product_name" className="text-stone-700">Product Name *</Label>
              <Input
                id="product_name"
                name="product_name"
                placeholder="e.g., PaymentPro Gateway"
                value={formData.product_name}
                onChange={handleChange}
                required
                data-testid="product-name-input"
                className="h-11"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-stone-700">Category</Label>
              <Select onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="category-select" className="h-11">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pitch Content */}
            <div className="space-y-2">
              <Label htmlFor="pitch_content" className="text-stone-700">Pitch Content *</Label>
              <Textarea
                id="pitch_content"
                name="pitch_content"
                placeholder="Describe your product, its benefits, pricing, and why this merchant should be interested..."
                value={formData.pitch_content}
                onChange={handleChange}
                required
                rows={6}
                data-testid="pitch-content-input"
                className="resize-none"
              />
              <p className="text-xs text-stone-400">
                Be specific about ROI, integration time, and key differentiators
              </p>
            </div>

            {/* Claims */}
            <div className="space-y-2">
              <Label className="text-stone-700">Key Claims (optional)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., 25% cost reduction"
                  value={newClaim}
                  onChange={(e) => setNewClaim(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addClaim())}
                  data-testid="claim-input"
                  className="h-10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addClaim}
                  disabled={!newClaim.trim() || formData.claims.length >= 5}
                  className="h-10 px-3"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.claims.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.claims.map((claim, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm"
                    >
                      {claim}
                      <button
                        type="button"
                        onClick={() => removeClaim(i)}
                        className="hover:text-violet-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-stone-100">
              <Button
                type="submit"
                disabled={loading || !formData.target_company_id || !formData.product_name || !formData.pitch_content}
                data-testid="submit-pitch-btn"
                className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Pitch
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </VendorLayout>
  );
}
